import React, { ReactElement, useEffect, useState } from 'react';
import WidgetActionsMenu from 'adapter/in/dashboards/pages/Widget/ActionsMenu';
import { WidgetType } from 'domain/entity/widget';
import ConfirmActionModal from 'adapter/in/common/components/ConfirmActionModal/ConfirmActionModal';
import i18n from 'asset/translation';
import gtm from 'adapter/in/reports/config/gtm';
import useBusinessInsight from 'adapter/in/common/widget/BusinessInsight/useBusinessInsight';
import { useCase } from 'adapter/in/common/hooks/useCase';
import { isSome } from 'fp-ts/lib/Option';
import { DeleteTextBlockCriteriaBuilder } from 'use-case/widgets/delete/deleteTextBlockCriteria';
import { dispatchReportLayoutsData } from 'adapter/in/reports/contexts/ReportLayoutDataContext';
import { ReadLayoutsCriteriaBuilder } from 'use-case/layout/read/readLayoutsCriteria';

type BusinessInsightActionsMenuProps = {
    widgetId: string;
    dashboardId: string;
    showMenu: boolean;
};

export const BusinessInsightActionsMenuContainer = (props: BusinessInsightActionsMenuProps): ReactElement => {
    const { widgetId, dashboardId, showMenu } = props;
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const { deleteBusinessInsight } = useBusinessInsight();
    const { execute: remove, error, isLoading, data } = useCase(deleteBusinessInsight, false);
    const { getLayouts } = dispatchReportLayoutsData();
    const [isRemoving, setIsRemoving] = useState(false);

    useEffect(() => {
        if (!isLoading && isRemoving && dashboardId) {
            getLayouts(ReadLayoutsCriteriaBuilder.create().withDashboardId(dashboardId).build());
        }
    }, [isLoading, isRemoving, dashboardId]);

    useEffect(() => {
        if (isSome(data)) {
            setIsRemoving(false);
        }
    }, [data]);

    const handleDelete = () => {
        remove(DeleteTextBlockCriteriaBuilder.create().withWidgetId(widgetId).withDashboardId(dashboardId).build());
        setIsRemoving(true);
        setShowDeleteModal(false);
    };

    return (
        <>
            <WidgetActionsMenu
                show={showMenu}
                showOptions={false}
                onDelete={() => setShowDeleteModal(true)}
                widgetType={WidgetType.BUSINESS_INSIGHT}
            />
            <ConfirmActionModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                mainButton={() => handleDelete()}
                hasError={isSome(error)}
                isLoading={isLoading}
                message={i18n.t('widgets.delete.warningMsg')}
                title={i18n.t('widgets.actionDelete')}
                mainButtonText={i18n.t('widgets.delete.yes')}
                mainButtonVariant='destructive'
                dataIdContext={{
                    main: gtm.deleteModal.delete,
                    cancel: gtm.deleteModal.cancel,
                    tryAgain: gtm.deleteModal.tryAgain,
                }}
            />
        </>
    );
};

export default BusinessInsightActionsMenuContainer;
